import { UploadDocumentCard } from './uploadDocuments/UploadDocumentCard'
import { useUploadDocumentsStep } from './uploadDocuments/useUploadDocumentsStep'
import { getUploadConfig } from './uploadDocumentConfig'
import { Button } from '@/shared/components'
import { StepHeader } from './StepHeader'
import { PartnerRequirementFields } from './uploadDocuments/PartnerRequirementFields'

interface UploadDocumentsStepProps {
  onNext: (options?: { skipUploadStep?: boolean }) => void
  onBack: () => void
  currentStep: number
  totalSteps: number
}

export function UploadDocumentsStep({
  onNext,
  onBack,
  currentStep,
  totalSteps
}: UploadDocumentsStepProps) {
  const {
    documentsQuery,
    getFileName,
    handleContinue,
    handleFileChange,
    handleRequirementChange,
    incompleteFieldRequirements,
    isUploading,
    isSavingRequirements,
    mvrUrl,
    requiredDocumentsQuery,
    requirementsError,
    requirementsQuery,
    requirementValues,
    uploadError,
    uploadingKeySet,
    validationError,
    visibleDocuments
  } = useUploadDocumentsStep(onNext)

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-5xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <div className="w-full text-center">
            <h1 className="modal-heading__title text-center">Complete Partner Requirements</h1>
            <p className="modal-heading__subtitle mx-auto mt-2 max-w-xl text-center text-lg text-gray-700">
              Complete only the details and documents that are still needed for your enrollment.
            </p>
          </div>
        </div>

        <div className="space-y-3 pt-6">
          {uploadError ? <p className="text-sm text-red-600">{uploadError}</p> : null}
          {validationError ? <p className="text-sm text-red-600">{validationError}</p> : null}
          {requirementsError ? <p className="text-sm text-red-600">{requirementsError}</p> : null}
          {requirementsQuery.isLoading ? (
            <p className="text-sm text-gray-600">Loading partner information...</p>
          ) : null}
          {requirementsQuery.isError ? (
            <p className="text-sm text-amber-700">
              Partner information could not be loaded. Please refresh and try again.
            </p>
          ) : null}
          {documentsQuery.isError ? (
            <p className="text-sm text-amber-700">
              Uploaded files could not be refreshed. Newly uploaded files will still appear after
              upload succeeds.
            </p>
          ) : null}
          {requiredDocumentsQuery.isLoading ? (
            <p className="text-sm text-gray-600">Loading required documents...</p>
          ) : null}
          {requiredDocumentsQuery.isError ? (
            <p className="text-sm text-amber-700">
              Required documents could not be loaded. Please refresh and try again.
            </p>
          ) : null}

          <PartnerRequirementFields
            requirements={incompleteFieldRequirements}
            values={requirementValues}
            onChange={handleRequirementChange}
          />

          {visibleDocuments.map((document, index) => {
            const uploadConfig = getUploadConfig(document)

            return (
              <UploadDocumentCard
                key={document.documentType}
                document={document}
                index={index}
                fileName={getFileName(document)}
                isUploading={uploadingKeySet.has(uploadConfig.key)}
                mvrUrl={mvrUrl}
                onFileChange={handleFileChange}
              />
            )
          })}

          <div
            className="animate-fade-in-up mx-auto flex max-w-md flex-col gap-2.5"
            style={{ animationDelay: '360ms' }}
          >
            <Button
              type="button"
              onClick={handleContinue}
              fullWidth
              disabled={isUploading || isSavingRequirements}
              loading={isUploading || isSavingRequirements}
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
